import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import Navigation from '@/components/Navigation';
import ExperienceCard from '@/components/ExperienceCard';
import EventCard from '@/components/EventCard';
import { Button } from '@/components/ui/button';
import QuickActionCard from '@/components/dashboard/QuickActionCard';
import { SectionHeader, ScrollRow, EmptyState } from '@/components/dashboard/SectionUtils';
import { formatShortLocation } from '@/utils/location';
import { getEntityImageUrl, makeImageFallback } from '@/lib/entityMedia';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Sparkles,
  ArrowRight,
  Calendar,
  MapPin,
  Users,
  Compass,
  RefreshCw,
  Tent,
  Mountain,
  Gift,
  BadgeCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Horizontal-scroll wrapper for mobile, regular grid on desktop
// (extracted helpers live in components/dashboard/)

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [myEntities, setMyEntities] = useState([]);
  const [featuredPartners, setFeaturedPartners] = useState([]);

  useEffect(() => {
    fetchRecommendations();
    fetchFeaturedPartners();
    seedIfNeeded();
    if (token) {
      fetchMyEntities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const seedIfNeeded = async () => {
    try {
      const response = await axios.get(`${API}/entities?limit=1`);
      if (response.data.total === 0) {
        await axios.post(`${API}/seed`);
        fetchRecommendations();
      }
    } catch (error) {
      console.error('Seed check error:', error);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/recommendations`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setRecommendations(response.data);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const getAIRecommendations = async () => {
    if (!user?.quiz_completed) {
      toast.info('Complete the quiz first for AI-powered recommendations!');
      navigate('/quiz');
      return;
    }
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/match`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      if (response.data.recommendations) {
        toast.success('AI recommendations ready!');
      }
      fetchRecommendations();
    } catch (error) {
      console.error('AI match error:', error);
      toast.error('Failed to get AI recommendations');
    } finally {
      setAiLoading(false);
    }
  };

  const fetchMyEntities = async () => {
    try {
      const response = await axios.get(`${API}/my-entities`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setMyEntities(response.data.entities || []);
    } catch (error) {
      console.error('Error fetching my entities:', error);
    }
  };

  const fetchFeaturedPartners = async () => {
    try {
      const response = await axios.get(`${API}/entities/featured?limit=12`);
      setFeaturedPartners(response.data?.items || []);
    } catch (error) {
      console.error('Error fetching featured partners:', error);
    }
  };

  const guides = recommendations?.guides || [];
  const events = recommendations?.events || [];
  const destinations = recommendations?.destinations || [];

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Navigation />

      <main className="px-4 pt-[68px] pb-8 sm:px-6 sm:pt-20 sm:pb-12 lg:px-8">
        <div className="mx-auto max-w-7xl">
          {/* Greeting */}
          <header className="pt-4 sm:pt-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              Welcome back
            </p>
            <h1 className="mt-1 text-2xl font-bold leading-tight sm:text-3xl md:text-4xl">
              {user?.name?.split(' ')[0] || 'Explorer'}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground sm:text-base">
              {user?.quiz_completed
                ? 'Personalized adventures for you'
                : 'Take the quiz to unlock personalized matches'}
            </p>
          </header>

          {/* Top action chips (small CTAs) */}
          <div className="mt-4 flex flex-wrap gap-2 sm:gap-3">
            {!user?.quiz_completed && (
              <Button
                size="sm"
                onClick={() => navigate('/quiz')}
                className="rounded-full"
                data-testid="take-quiz-btn"
              >
                <Sparkles className="mr-1.5 h-4 w-4" />
                Take Quiz
              </Button>
            )}
            <Button
              size="sm"
              variant={user?.quiz_completed ? 'default' : 'outline'}
              onClick={getAIRecommendations}
              disabled={aiLoading}
              className="rounded-full"
              data-testid="ai-match-btn"
            >
              {aiLoading ? (
                <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-1.5 h-4 w-4" />
              )}
              AI Match
            </Button>
            {user?.quiz_completed && user?.quiz_responses?.experience_level === 'beginner' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/welcome-kit')}
                className="rounded-full"
                data-testid="welcome-kit-btn"
              >
                <Gift className="mr-1.5 h-4 w-4" />
                Welcome Kit
              </Button>
            )}
          </div>

          {/* Quick Actions (above the fold on mobile) */}
          <section className="mt-6 sm:mt-10" aria-label="Quick actions">
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-3 sm:gap-5">
              <QuickActionCard
                icon={MapPin}
                title="Explore Map"
                subtitle="Trails, camps & guides near you"
                onClick={() => navigate('/discovery?view=map')}
                testid="quick-action-map"
              />
              <QuickActionCard
                icon={Compass}
                title="Plan Route"
                subtitle="Build your next adventure"
                onClick={() => navigate('/route-planner')}
                testid="quick-action-route"
              />
              <QuickActionCard
                icon={Gift}
                title="Welcome Kit"
                subtitle="Gear, safety & first steps"
                onClick={() => navigate('/welcome-kit')}
                testid="quick-action-kit"
              />
            </div>
          </section>

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center py-16 sm:py-20">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <>
              {/* My Entities */}
              {myEntities.length > 0 && (
                <section className="mt-10 sm:mt-14">
                  <SectionHeader
                    icon={Tent}
                    title="My Partners"
                    onViewAll={() => navigate('/discovery')}
                    viewAllTestId="view-all-my-entities-btn"
                  />
                  <ScrollRow>
                    {myEntities.map((entity) => (
                      <button
                        key={entity.entity_id}
                        type="button"
                        onClick={() => navigate(`/entity/${entity.entity_id}`)}
                        className="group block w-full overflow-hidden rounded-2xl border border-border/50 bg-card text-left transition-all hover:shadow-lg active:scale-[0.99]"
                      >
                        <div className="relative h-36 overflow-hidden bg-muted sm:h-44">
                          {(() => {
                            const src = getEntityImageUrl(entity, { width: 600 });
                            if (!src) {
                              return (
                                <div className="flex h-full w-full items-center justify-center">
                                  <Mountain className="h-12 w-12 text-muted-foreground" />
                                </div>
                              );
                            }
                            return (
                              <img
                                src={src}
                                alt={entity.name}
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                onError={makeImageFallback(entity)}
                              />
                            );
                          })()}
                          {entity.verified && (
                            <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-primary/90 px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
                              <BadgeCheck className="h-3 w-3" />
                              Verified
                            </span>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="truncate text-base font-semibold group-hover:text-primary">
                            {entity.name}
                          </h3>
                          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground sm:text-sm">
                            {entity.description}
                          </p>
                          {entity.location && (
                            <span className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              {formatShortLocation(entity)}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </ScrollRow>
                </section>
              )}

              {/* Featured Partners — curated by Super Admin */}
              <section className="mt-10 sm:mt-14" data-testid="featured-partners-section">
                <SectionHeader
                  icon={Sparkles}
                  title="Featured Partners"
                  onViewAll={() => navigate('/discovery')}
                  viewAllTestId="view-all-featured-btn"
                />
                {featuredPartners.length > 0 ? (
                  <ScrollRow>
                    {featuredPartners.slice(0, 12).map((p) => (
                      <ExperienceCard key={p.entity_id} entity={p} />
                    ))}
                  </ScrollRow>
                ) : (
                  <EmptyState
                    icon={Sparkles}
                    message="No featured partners yet. Super admins can curate this rail from the Admin Panel → Partners."
                  />
                )}
              </section>

              {/* Upcoming Events */}
              <section className="mt-10 sm:mt-14">
                <SectionHeader
                  icon={Calendar}
                  title="Upcoming Events"
                  onViewAll={() => navigate('/discovery?type=event')}
                  viewAllTestId="view-all-events-btn"
                  accent="text-accent"
                />
                {events.length > 0 ? (
                  <ScrollRow>
                    {events.slice(0, 6).map((event) => (
                      <EventCard key={event.event_id} event={event} />
                    ))}
                  </ScrollRow>
                ) : (
                  <EmptyState icon={Calendar} message="No upcoming events" />
                )}
              </section>

              {/* Destinations */}
              <section className="mt-10 sm:mt-14">
                <SectionHeader
                  icon={MapPin}
                  title="Campgrounds & Resorts"
                  onViewAll={() => navigate('/discovery?type=campgrounds_resorts')}
                  viewAllTestId="view-all-destinations-btn"
                  accent="text-green-500"
                />
                {destinations.length > 0 ? (
                  <ScrollRow>
                    {destinations.slice(0, 6).map((dest) => (
                      <ExperienceCard key={dest.entity_id} entity={dest} />
                    ))}
                  </ScrollRow>
                ) : (
                  <EmptyState icon={Tent} message="No destinations available yet" />
                )}
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
